import css from './EventsCalendar.module.scss';
import EventsCalendarWidget from '../../../../widgets/eventsCalendarOffice/EventsCalendarWidget';
import T from '../../../../common/components/T';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserContext from '../../../../common/utils/getContext';
import Plug from '../../../../widgets/plug/Plug/Plug';
import no_events from '../../../../public/src/img/plugs/no_events.svg';
import Skeleton from '../../../../widgets/plug/Skeleton/Skeleton';
import { skeleton } from './helpers';
import { getData, navigateV2 } from '../../../../common/utils/router';
import { Tooltip, svgSprite } from '../../../../hybrid/wrappers';
import moment from 'moment';
import classNames from 'classnames';
import Event from './components/Event/Event';
import CreateEvent from '../../../../widgets/createEvent/CreateEvent';
import { useWindowDimensions } from '../../../../common/utils/windowDimensions';

const RenderData = (props) => {
    const { response } = props;
    const { lang, userToken } = useContext(UserContext);
    const [reloadData, setReloadData] = useState(false);

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const firstDay = useMemo(
        () => moment(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)).format('YYYY-MM-DD'),
        [currentMonth]
    );

    const lastDay = useMemo(
        () => moment(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)).format('YYYY-MM-DD'),
        [currentMonth]
    );

    const [events, setEvents] = useState(response);
    const date = new Date();

    // ставимо за замовчуванням обрану дату сьогодні
    const [selectedDay, setSelectedDay] = useState(date);
    const dayMonth = useMemo(() => moment(selectedDay).format('DD MMMM'), [lang, selectedDay]);
    const weekday = useMemo(() => moment(selectedDay).format('dddd'), [lang, selectedDay]);

    const normalizedDate = useMemo(() => `${dayMonth}, ${weekday}`, [dayMonth, weekday]);

    const todayEvents = useMemo(() => {
        return events?.data?.filter((event) => {
            return moment(event?.event_date, 'YYYY-MM-DD').date() === new Date(selectedDay).getDate();
        });
    }, [selectedDay, events]);

    // дні коли є події
    const datesWithEvents = useMemo(() => {
        return events?.data?.map((event) => new Date(event?.event_date));
    }, [events]);

    // модальне вікно створення події
    const [createEventModal, showCreateEventModal] = useState(false);

    // обробник кліку на день календаря
    const selectHandler = (date) => {
        if (date) {
            setSelectedDay(date);
        }
    };

    const onMonthChange = (newMonth) => {
        setEvents(null);
        setCurrentMonth(newMonth);
    };

    const createEventHandler = useCallback(() => {
        showCreateEventModal({ date: selectedDay });
    }, [selectedDay]);

    // const userHasSuccess = useMemo(() => {
    //     return roles?.some((role) => ['superadmin', 'admin', 'gtuser::content_manager'].includes(role.name));
    // }, [roles]);

    const userHasSuccess = useMemo(() => true, []);

    useEffect(() => {
        if (events) return;
        getData(`/api/v1/user/event-calendar?filter[date_start]=${firstDay}&filter[date_end]=${lastDay}`, userToken, lang).then(
            setEvents
        );
    }, [userToken, lang, firstDay, lastDay, reloadData, events]);

    const isMobile = useWindowDimensions() < 768;

    return (
        <>
            <div className={css.block}>
                <div className={css.blockCalendar}>
                    <EventsCalendarWidget
                        onSelect={selectHandler}
                        onMonthChange={onMonthChange}
                        modifiers={{ booked: datesWithEvents }}
                        modifiersClassNames={{
                            booked: 'rdp-event'
                        }}
                        selected={selectedDay}
                        className='eventsCalendarOffice'
                    />
                </div>
                <div className={css.blockEvents}>
                    <div className={css.head}>
                        <div className={css.headTitle}>
                            <p className={css.title}>
                                <T textName='events-calendar' defaultStr='Календарь событий' page='/office' />
                            </p>
                            <div className={css.undertitle}>
                                <p className={css.date}>{normalizedDate}</p>
                            </div>
                        </div>
                        {userHasSuccess ? (
                            <div className={css.headButtons}>
                                <Tooltip
                                    showModalTooltip={!isMobile}
                                    disableOnMobile={true}
                                    text={<T textName='add-event' defaultStr='Добавить событие' />}
                                >
                                    <div onClick={createEventHandler} className={classNames('circleButton', css.headButton)}>
                                        {svgSprite('plus')}
                                    </div>
                                </Tooltip>
                                <Tooltip
                                    showModalTooltip={!isMobile}
                                    disableOnMobile={true}
                                    text={<T textName='more-details' defaultStr='Подробнее' page='/office' />}
                                >
                                    <div
                                        onClick={() => navigateV2('/calendar')}
                                        className={classNames('circleButton', css.headButton, css.headButtonArrow)}
                                    >
                                        {svgSprite('arrow-right')}
                                    </div>
                                </Tooltip>
                            </div>
                        ) : null}
                    </div>
                    <div className={classNames(css.events, { [css.eventsHeight]: todayEvents && todayEvents?.length })}>
                        {todayEvents && todayEvents?.length ? (
                            <div className={css.eventsList}>
                                {todayEvents.map((event) => (
                                    <Event
                                        showCreateEventModal={showCreateEventModal}
                                        key={event.id}
                                        event={event}
                                        setReloadData={setReloadData}
                                        setEvents={setEvents}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Plug
                                plugTitle={
                                    <T textName='no-scheduled-events' defaultStr='Нет запланированных событий' page='/office' />
                                }
                                plugDescription={
                                    <T
                                        textName='add-new-event-to-your-calendar'
                                        defaultStr='Добавьте новые события в свой календарь'
                                        page='/office'
                                    />
                                }
                                plugImg={no_events}
                                classes={{ plugContainer: [css.plugContainer] }}
                            />
                        )}
                    </div>
                </div>
            </div>
            {createEventModal ? (
                <CreateEvent
                    closeHandler={() => showCreateEventModal(false)}
                    data={createEventModal}
                    events={events}
                    onSuccess={() => {
                        setEvents(null);
                        setReloadData((state) => !state);
                    }}
                />
            ) : null}
        </>
    );
};

export default function EventsCalendar() {
    const { userToken, lang } = useContext(UserContext);

    const firstDay = useMemo(() => moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'), []);

    const lastDay = useMemo(
        () => moment(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)).format('YYYY-MM-DD'),
        []
    );

    const loader = useCallback(
        () => getData(`/api/v1/user/event-calendar?filter[date_start]=${firstDay}&filter[date_end]=${lastDay}`, userToken, lang),
        [userToken, firstDay, lastDay, lang]
    );

    return <Skeleton Component={RenderData} handler={loader} lazy={true} pattern={skeleton} wrapperStyles={css.skeleton} />;
}
